import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AUTH } from '@/constants/testIds';
import { useAuth } from '@/components/AuthProvider';
import { AuthShell } from '@/components/AuthShell';
import { api, setToken } from '@/lib/api';
import { PasswordField } from '@/components/ui/money-field';
import {
  firebaseEmailLogin,
  firebaseSendVerificationEmail,
  isFirebaseClientConfigured,
  mapFirebaseAuthError,
} from '@/lib/firebaseClient';

export default function SignupPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const intentId = params.get('intentId') || '';
  const { refresh } = useAuth();
  const [form, setForm] = useState({
    company: '',
    name: '',
    email: '',
    password: '',
    inviteCode: '',
    acceptTerms: false,
    website: '', // honeypot
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const firebaseReady = isFirebaseClientConfigured();

  const foundingPath = useMemo(() => Boolean(intentId), [intentId]);

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!firebaseReady) {
      setError('Firebase is not configured on this build.');
      return;
    }
    if (!form.acceptTerms) {
      setError('Accept the Terms and Privacy Policy to continue.');
      return;
    }
    if (form.password.length < 10) {
      setError('Password must be at least 10 characters.');
      return;
    }
    setLoading(true);
    setError('');
    setInfo('');
    try {
      const r = await api('/api/v1/public/signup', {
        method: 'POST',
        token: null,
        body: {
          companyName: form.company,
          email: form.email.trim(),
          password: form.password,
          displayName: form.name || form.email.trim(),
          acceptTerms: true,
          inviteCode: form.inviteCode.trim() || undefined,
          intentId: intentId || undefined,
          website: form.website,
        },
      });
      if (r.status !== 201) {
        setError(String(r.data?.error ?? 'Signup failed'));
        return;
      }
      setInfo('Account created. Verify your email before the board unlocks.');
      if (r.data?.verificationLink) {
        await navigator.clipboard.writeText(r.data.verificationLink).catch(() => undefined);
        setInfo((prev) => `${prev} (Dev link copied.)`);
      }
      const idToken = await firebaseEmailLogin(form.email.trim(), form.password);
      setToken(idToken);
      await api('/api/v1/auth/session', { method: 'POST', token: idToken, body: {} });
      try {
        await firebaseSendVerificationEmail();
      } catch {
        /* admin-generated link may already exist */
      }
      await refresh();
      navigate('/', { replace: true });
    } catch (err) {
      setError(mapFirebaseAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      eyebrow={foundingPath ? 'Founding setup' : 'Company onboarding'}
      headline={
        foundingPath
          ? 'Activate your founding workspace.'
          : 'Create a company — then verify before you operate.'
      }
      subhead={
        foundingPath
          ? 'Finish account setup from your founding checkout. Email verification is required before dispatch or finance screens open.'
          : 'Prefer the founding program for new carriers. Direct signup needs a work email, accepted terms, and inbox verification.'
      }
    >
      <div className="space-y-5">
        {!foundingPath ? (
          <div className="rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 text-sm leading-relaxed">
            <p className="font-medium text-foreground">Starting a new carrier company?</p>
            <p className="text-muted-foreground mt-1">
              Join founding rates and trial at{' '}
              <a
                href="https://logisticslash.com/founding"
                className="text-primary hover:underline font-medium"
                target="_blank"
                rel="noreferrer"
              >
                logisticslash.com/founding
              </a>
              , then return here to finish setup.
            </p>
          </div>
        ) : null}

        <div className="space-y-2">
          <h2 className="text-2xl font-bold tracking-tight">
            {foundingPath ? 'Complete founding signup' : 'Create company account'}
          </h2>
          <p className="text-sm text-muted-foreground">
            Work email required. Dashboard stays locked until you verify.
          </p>
        </div>

        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        {info ? <p className="text-sm text-primary">{info}</p> : null}

        <form onSubmit={handleSignup} className="space-y-4">
          {/* honeypot */}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={(e) => setForm({ ...form, website: e.target.value })}
            className="absolute opacity-0 pointer-events-none h-0 w-0"
            aria-hidden
          />

          <div className="space-y-2">
            <Label htmlFor="company">Company name</Label>
            <Input
              id="company"
              data-testid={AUTH.signupCompany}
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="name">Your name</Label>
            <Input
              id="name"
              data-testid={AUTH.signupName}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Work email</Label>
            <Input
              id="email"
              type="email"
              data-testid={AUTH.signupEmail}
              placeholder="you@carrier.com"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
          </div>
          <div className="space-y-2">
            <PasswordField
              id="password"
              label="Password (10+ characters)"
              minLength={10}
              autoComplete="new-password"
              data-testid={AUTH.signupPassword}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
            />
          </div>
          {!foundingPath ? (
            <div className="space-y-2">
              <Label htmlFor="invite">Invite code (if required)</Label>
              <Input
                id="invite"
                value={form.inviteCode}
                onChange={(e) => setForm({ ...form, inviteCode: e.target.value })}
                placeholder="Optional unless your admin requires it"
              />
            </div>
          ) : null}

          <label className="flex items-start gap-2.5 text-sm text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 rounded border-border"
              checked={form.acceptTerms}
              onChange={(e) => setForm({ ...form, acceptTerms: e.target.checked })}
              required
            />
            <span>
              I agree to the{' '}
              <a
                href="https://logisticslash.com/terms"
                className="text-primary hover:underline"
                target="_blank"
                rel="noreferrer"
              >
                Terms
              </a>{' '}
              and{' '}
              <a
                href="https://logisticslash.com/privacy"
                className="text-primary hover:underline"
                target="_blank"
                rel="noreferrer"
              >
                Privacy Policy
              </a>
              . I understand email verification is required before using the TMS.
            </span>
          </label>

          <Button
            type="submit"
            className="w-full"
            data-testid={AUTH.signupSubmit}
            disabled={loading || !firebaseReady}
          >
            {loading ? 'Creating…' : 'Create account & verify email'}
          </Button>
        </form>

        <p className="text-center text-sm text-muted-foreground">
          Already verified?{' '}
          <Link to="/login" className="text-primary hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </AuthShell>
  );
}
